/**
 * The filter tree, client side. Mirrors server/filters.js: groups and rules live
 * in the same `rules` array and a group is the node that has one, so
 * `A and (B or C)` is a root AND group with a nested OR group.
 *
 * Nodes carry a client-only `id` for React keys and in-place edits; it is
 * stripped before anything is compared or saved.
 */

/** How deep the UI lets you nest. The server accepts more; nobody needs it. */
export const MAX_GROUP_DEPTH = 4

let nextId = 1
export const newId = () => nextId++

export const isGroup = (node) => !!node && Array.isArray(node.rules)

export const emptySpec = () => ({ id: newId(), combinator: 'AND', rules: [] })

const flip = (combinator) => (combinator === 'AND' ? 'OR' : 'AND')

/** A new rule must start on an operator that is valid for its column's kind. */
export function newRule(columns, operators) {
  const column = columns[0]
  const ops = operators.filter((o) => o.kinds.includes(column?.kind ?? 'text'))
  return {
    id: newId(),
    column: column?.name ?? '',
    op: ops.find((o) => o.id === 'contains')?.id ?? ops[0]?.id ?? 'equals',
    value: '',
    value2: '',
    values: [],
    caseSensitive: false,
  }
}

/**
 * A group starts on the opposite combinator to its parent — the reason to open
 * one is almost always "…and any of these", not "…and all of these".
 */
export function newGroup(columns, operators, parentCombinator) {
  return {
    id: newId(),
    combinator: flip(parentCombinator === 'OR' ? 'OR' : 'AND'),
    rules: [newRule(columns, operators)],
  }
}

/** Rules only — groups are structure, not conditions. */
export const countRules = (node) =>
  !node ? 0 : isGroup(node) ? node.rules.reduce((sum, child) => sum + countRules(child), 0) : 1

/** Groups the user added, not counting the root. */
export const countGroups = (node) =>
  !node || !isGroup(node) ? 0 : node.rules.reduce((sum, child) => sum + countGroups(child) + (isGroup(child) ? 1 : 0), 0)

export const depthOf = (node) =>
  !isGroup(node) ? 0 : 1 + Math.max(0, ...node.rules.map(depthOf))

/**
 * Strip ids and drop what cannot narrow anything — empty groups, and groups
 * that only hold empty groups — the same way the server does before it compiles.
 * Returns null when the spec matches every row.
 *
 * Rules are rebuilt field by field rather than spread, so the result is
 * canonical: two specs that select the same rows stringify identically, whatever
 * order their keys happen to be in or how a list of values was ticked.
 */
export function normalizeSpec(node) {
  if (isGroup(node)) {
    const rules = node.rules.map(normalizeSpec).filter(Boolean)
    if (rules.length === 0) return null
    if (rules.length === 1) return rules[0]
    return { combinator: node.combinator === 'OR' ? 'OR' : 'AND', rules }
  }
  if (!node?.column || !node.op) return null
  return {
    column: node.column,
    op: node.op,
    value: node.value ?? '',
    value2: node.value2 ?? '',
    values: [...(node.values ?? [])].sort(),
    caseSensitive: !!node.caseSensitive,
  }
}

/** Comparable string form: two specs with the same signature return the same rows. */
export const specSignature = (spec) => JSON.stringify(normalizeSpec(spec))

/** Does this spec actually filter, or is it "every row"? */
export const hasFilters = (spec) => normalizeSpec(spec) !== null

/**
 * What goes on the wire: pruned, id-free, and always a group at the root, so the
 * request body (and the job cache key it produces) has one canonical shape.
 */
export function toRequestSpec(spec) {
  const pruned = normalizeSpec(spec)
  if (!pruned) return null
  return isGroup(pruned) ? pruned : { combinator: 'AND', rules: [pruned] }
}

/** One-line description of a filter tree, matching the server's summarize(). */
export function describeFilters(spec) {
  const rules = countRules(spec)
  if (rules === 0) return 'all rows'
  const groups = countGroups(spec)
  const count = `${rules} filter${rules === 1 ? '' : 's'}`
  return groups > 0 ? `${count} in ${groups} group${groups === 1 ? '' : 's'}` : count
}

/**
 * Rebuild a saved spec against the columns the file has now: unknown columns are
 * dropped rather than sent to the server, which would reject the whole query.
 * Group structure survives; a group left empty goes with it.
 *
 * @returns {{ spec: object, dropped: number }}
 */
export function reconcileSpec(spec, headers) {
  const known = new Set(headers)
  let dropped = 0

  const walk = (node) => {
    if (isGroup(node)) {
      const rules = node.rules.map(walk).filter(Boolean)
      if (rules.length === 0) return null
      return { id: newId(), combinator: node.combinator === 'OR' ? 'OR' : 'AND', rules }
    }
    if (!node?.column || !known.has(node.column)) {
      dropped++
      return null
    }
    return { value: '', value2: '', values: [], caseSensitive: false, ...node, id: newId() }
  }

  const root = walk({ combinator: spec?.combinator, rules: spec?.rules ?? [] })
  return {
    spec: isGroup(root)
      ? root
      : { id: newId(), combinator: spec?.combinator === 'OR' ? 'OR' : 'AND', rules: root ? [root] : [] },
    dropped,
  }
}
